/**
 * @copyright Copyright 2024-2024 Epic Systems Corporation
 * @file Questionnaire Response component
 * @module Epic.AppOrchard.Core.Questionnaire
 */

import { WarningTwoIcon } from "@chakra-ui/icons";
import {
	Box,
	Checkbox,
	CheckboxGroup,
	FormControl,
	FormLabel,
	Heading,
	Radio,
	RadioGroup,
	Stack,
	Text,
} from "@chakra-ui/react";
import { AOTooltip, TextareaWithRemainingChars } from "ao/components/Core";
import {
	IQuestionnaireCategoryForEditingWithDisplayValues,
	IQuestionnaireOptionForEditing,
	IQuestionnaireQuestionForEditing,
	IQuestionnaireQuestionForEditingWithComputedDisplayValues,
	QuestionnaireQuestionType,
} from "ao/types";
import { ChangeEvent, FC, Fragment, FunctionComponent, memo, useEffect, useMemo, useState } from "react";
import { BsFillQuestionCircleFill } from "react-icons/bs";
import { WarningMessage } from "../Core/WarningMessage";

interface IProps {
	heading?: string;
	description?: string;
	showCategoryHeaders?: boolean;
	isDisabled?: boolean;
	tooltip?: string;
	allowEmptyQuestions?: boolean;
	categories: IQuestionnaireCategoryForEditingWithDisplayValues[];
	answerChangeHandler: (
		categoryId: number,
		question: IQuestionnaireQuestionForEditing,
		option?: IQuestionnaireOptionForEditing,
		optionIsSelected?: boolean,
		freetext?: string,
	) => void;
}

export const SingleSaveQuestionnaire: FunctionComponent<IProps> = memo((props: IProps) => {
	const {
		heading,
		description,
		showCategoryHeaders,
		isDisabled,
		tooltip,
		allowEmptyQuestions,
		categories,
		answerChangeHandler,
	} = props;
	const anyQuestions = categories?.some((category) => category.questions?.length > 0);

	return anyQuestions ? (
		<>
			{heading && (
				<Heading as="h2" mt={"1em"}>
					{heading}&nbsp;
					{tooltip && (
						<AOTooltip helptext={tooltip}>
							<BsFillQuestionCircleFill />
						</AOTooltip>
					)}
				</Heading>
			)}

			{description && <Text>{description}</Text>}

			{categories.map((category) => (
				<CategorySection
					category={category}
					showHeader={!!showCategoryHeaders}
					isDisabled={!!isDisabled}
					answerChangeHandler={answerChangeHandler}
					allowEmptyQuestions={allowEmptyQuestions ?? false}
				/>
			))}
		</>
	) : (
		<></>
	);
});

interface ICategoryProps {
	category: IQuestionnaireCategoryForEditingWithDisplayValues;
	showHeader: boolean;
	isDisabled: boolean;
	allowEmptyQuestions: boolean;
	answerChangeHandler: (
		categoryId: number,
		question: IQuestionnaireQuestionForEditing,
		option?: IQuestionnaireOptionForEditing,
		optionIsSelected?: boolean,
		freetext?: string,
	) => void;
}

const CategorySection: FC<ICategoryProps> = memo((props: ICategoryProps) => {
	const { category, showHeader, isDisabled, allowEmptyQuestions, answerChangeHandler } = props;

	const anyQuestionsVisible =
		category.questions?.length > 0 && category.questions.some((question) => question.shouldShow);

	return anyQuestionsVisible ? (
		<>
			{showHeader && (
				<>
					<Heading as="h3">{category.title}&nbsp;</Heading>
					<Text>{category.description}</Text>
				</>
			)}
			{category.questions.map(
				(question: IQuestionnaireQuestionForEditingWithComputedDisplayValues, index: number) => (
					<Question
						key={index}
						question={question}
						categoryId={category.id}
						isReadOnly={isDisabled}
						answerChangeHandler={answerChangeHandler}
						hideOtherOptions={isDisabled}
						allowEmptyQuestions={allowEmptyQuestions}
					></Question>
				),
			)}
		</>
	) : null;
});

const labelStyleProps = {
	color: "#3f7d67",
	fontWeight: "normal",
	display: "flex",
	alignItems: "center",
	paddingBottom: "5px",
	margin: "0px",
};

interface IQuestionProps {
	question: IQuestionnaireQuestionForEditingWithComputedDisplayValues;
	categoryId: number;
	isReadOnly: boolean;
	allowEmptyQuestions: boolean;
	answerChangeHandler: (
		categoryId: number,
		question: IQuestionnaireQuestionForEditingWithComputedDisplayValues,
		option?: IQuestionnaireOptionForEditing,
		optionIsSelected?: boolean,
		freetext?: string,
	) => void;
	hideOtherOptions?: boolean;
}

const Question: FC<IQuestionProps> = memo((props: IQuestionProps) => {
	const {
		question,
		categoryId,
		isReadOnly,
		allowEmptyQuestions,
		answerChangeHandler,
		hideOtherOptions,
	} = props;

	const [exclusiveCheckbox, setExclusiveCheckbox] = useState<number>(0);

	const selectedRadioOption =
		question.type === QuestionnaireQuestionType.Radio && question.options && question.options.length > 0
			? question.options.find((option: IQuestionnaireOptionForEditing) => option.selected)
			: undefined;

	useEffect(() => {
		if (question && question.type === QuestionnaireQuestionType.Checkboxes) {
			setExclusiveCheckbox(0);
			question.options.forEach((option) => {
				if (option.selected && option.override) {
					setExclusiveCheckbox(option.id);
				}
			});
		}
	}, [question]);

	const showError = useMemo(() => {
		if (!allowEmptyQuestions) {
			switch (question.type) {
				case QuestionnaireQuestionType.Freetext:
					return !question.responseText || !question.responseText.trim();
				case QuestionnaireQuestionType.Radio:
					return (
						!selectedRadioOption ||
						(selectedRadioOption.supportsFreetext &&
							(!selectedRadioOption.freetextResponse ||
								!selectedRadioOption.freetextResponse.trim()))
					);
				case QuestionnaireQuestionType.Checkboxes:
					const selectedOptions = question.options.filter((option) => option.selected);
					if (selectedOptions.length === 0) {
						return true;
					}
					const selectedOptionsMissingFreetext = selectedOptions.filter(
						(option) =>
							option.supportsFreetext &&
							(!option.freetextResponse || !option.freetextResponse.trim()),
					);
					return selectedOptionsMissingFreetext.length > 0;
			}
		}
	}, [allowEmptyQuestions, question.options, question.responseText, question.type, selectedRadioOption]);

	const options = useMemo(() => {
		if (question.type === QuestionnaireQuestionType.Checkboxes) {
			return question.options
				.filter((option) => {
					return !isReadOnly || !hideOtherOptions || option.selected;
				})
				.map((option) => (
					<Fragment key={option.id}>
						<Checkbox
							isDisabled={
								isReadOnly || (exclusiveCheckbox > 0 && exclusiveCheckbox !== option.id)
							}
							paddingLeft="4px"
							spacing="0.2rem"
							isChecked={option.selected}
							onChange={(e) => {
								answerChangeHandler(
									categoryId,
									question,
									option,
									e.target.checked,
									option.freetextResponse,
								);
							}}
						>
							{option.text}
						</Checkbox>
						{option.supportsFreetext && option.selected && (
							<TextareaWithRemainingChars
								rows={1}
								cols={20}
								maxChars={1000}
								maxLength={1000}
								placeholder="Enter response here"
								data-testid={"textbox-for-question-" + question.id + "-checkbox-" + option.id}
								isReadOnly={isReadOnly}
								isInvalid={!option.freetextResponse}
								value={option.freetextResponse}
								onChange={(ev: ChangeEvent<HTMLTextAreaElement>) => {
									answerChangeHandler(
										categoryId,
										question,
										option,
										option.selected,
										ev.target.value,
									);
								}}
							/>
						)}
						{option.warningText && option.selected && (
							<WarningMessage message={option.warningText} />
						)}
					</Fragment>
				));
		} else if (question.type === QuestionnaireQuestionType.Radio) {
			return question.options
				.filter((option) => {
					return !isReadOnly || !hideOtherOptions || option.selected;
				})
				.map((option) => (
					<Fragment key={option.id}>
						<Radio isDisabled={isReadOnly} value={option.id.toString()} isFocusable>
							{option.text}
						</Radio>
						{option.supportsFreetext && option.selected && (
							<TextareaWithRemainingChars
								rows={1}
								cols={20}
								maxChars={1000}
								maxLength={1000}
								placeholder="Enter response here"
								data-testid={"textbox-for-question-" + question.id + "-radio-" + option.id}
								isReadOnly={isReadOnly}
								isInvalid={!option.freetextResponse}
								value={option.freetextResponse ?? ""}
								onChange={(ev: ChangeEvent<HTMLTextAreaElement>) => {
									answerChangeHandler(categoryId, question, option, true, ev.target.value);
								}}
							/>
						)}
						{option.warningText && option.selected && (
							<WarningMessage message={option.warningText} />
						)}
					</Fragment>
				));
		} else {
			return [];
		}
	}, [question, isReadOnly, hideOtherOptions, exclusiveCheckbox, answerChangeHandler, categoryId]);

	return question.shouldShow ? (
		<FormControl isInvalid={showError} py="5px" pl={question.indent ? "30px" : ""}>
			<FormLabel {...labelStyleProps}>
				{showError && <WarningTwoIcon w={4} h={4} color="red.500" mr="0.33em" />}
				<Box dangerouslySetInnerHTML={{ __html: question.text }} />
				{question.helptext && (
					<>
						&nbsp;
						<AOTooltip
							helptext={<Box dangerouslySetInnerHTML={{ __html: question.helptext }}></Box>}
						>
							<BsFillQuestionCircleFill />
						</AOTooltip>
					</>
				)}
			</FormLabel>
			{question.type === QuestionnaireQuestionType.Freetext && (
				<TextareaWithRemainingChars
					isReadOnly={isReadOnly}
					rows={1}
					cols={20}
					maxChars={1000}
					maxLength={1000}
					placeholder="Enter response here"
					value={question.responseText}
					onChange={(ev: ChangeEvent<HTMLTextAreaElement>) => {
						answerChangeHandler(categoryId, question, undefined, undefined, ev.target.value);
					}}
				/>
			)}
			{question.type === QuestionnaireQuestionType.Radio && (
				<RadioGroup
					value={selectedRadioOption?.id?.toString()}
					onChange={(newValue: string) => {
						const newlySelectedOption = question.options.find(
							(option: IQuestionnaireOptionForEditing) => option.id.toString() === newValue,
						);

						answerChangeHandler(
							categoryId,
							question,
							newlySelectedOption,
							true,
							newlySelectedOption?.freetextResponse,
						);
					}}
				>
					<Stack spacing={1} direction={"column"}>
						{options}
					</Stack>
				</RadioGroup>
			)}
			{question.type === QuestionnaireQuestionType.Checkboxes && (
				<CheckboxGroup>
					<Stack spacing={1} direction={"column"}>
						{options}
					</Stack>
				</CheckboxGroup>
			)}
		</FormControl>
	) : null;
});
